import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getInstanceDirectory } from '../../portal/instanceDirectory';
import { formatPortalInstanceName, type PortalInstance } from '../../portal/portalClient';
import { usePortalAccess } from './usePortalAccess';

const STALE_TIME = 5 * 60 * 1000;

export type InstanceLabelState = {
  id: string;
  instance?: PortalInstance;
  isResolved: boolean;
  isEnabled: boolean;
};

/**
 * What `instanceQueryKey` holds in the React Query cache: the portal record, or `null`
 * for "the portal answered and this id is not in the caller's scope".
 *
 * Never `undefined`: React Query v5 rejects an `undefined` query result, and
 * `setQueryData(key, undefined)` silently no-ops, which would leave the query looking
 * perpetually pending instead of resolved-absent. Callers writing into this cache
 * (via `queryClient.setQueryData(instanceQueryKey(...), ...)`) must use `null` to
 * indicate "definitively not found", not `undefined`.
 */
export type CachedPortalInstance = PortalInstance | null;

export const instanceQueryKey = (baseUrl: string, id: string) => ['portal-instance', baseUrl, id];

/** Display label for an instance id: the portal name, `Unknown Instance (<id>)`, or the raw id. */
export const formatInstanceLabel = ({ id, instance, isResolved, isEnabled }: InstanceLabelState): string => {
  if (!isEnabled) return id;
  const name = instance ? formatPortalInstanceName(instance) : '';
  if (name) return name;
  return isResolved ? `Unknown Instance (${id})` : id;
};

const instanceQueryOptions = (baseUrl: string, token: string, id: string, canQuery: boolean) =>
  queryOptions({
    queryKey: instanceQueryKey(baseUrl, id),
    queryFn: async () => {
      const result = await getInstanceDirectory(baseUrl, token).load(id);
      // Convert undefined to null; see CachedPortalInstance for the cache contract.
      return result ?? null;
    },
    enabled: canQuery && !!id,
    staleTime: STALE_TIME,
    // One attempt: the label already reads as the raw id while we wait, so retrying three
    // times only delays the fallback the user is already looking at.
    retry: false,
  });

/**
 * Resolve one instance id to a display label.
 *
 * Resolution keys off `isSuccess`, never `isFetched`: after a failed fetch `isFetched` is
 * also true, which would relabel every instance "Unknown Instance" during a portal outage
 * or CORS rejection. A failure has to look like "no name yet", not "no such instance".
 */
export const useInstanceLabel = (id: string) => {
  const { baseUrl, token, canQuery, isPortalSource } = usePortalAccess();
  const { data, isSuccess } = useQuery(instanceQueryOptions(baseUrl, token, id, canQuery));

  return useMemo(() => {
    // `isEnabled` here means "the portal is actually the source" (`isPortalSource`), not just
    // "the flag is on" — flag-on-with-no-host must fall back to the raw id like flag-off,
    // not report enabled while the query sits permanently disabled. See usePortalAccess.
    const state: InstanceLabelState = {
      id,
      instance: data ?? undefined,
      isResolved: isSuccess,
      isEnabled: isPortalSource,
    };
    return { ...state, label: formatInstanceLabel(state) };
  }, [id, data, isSuccess, isPortalSource]);
};

/**
 * Resolve many ids at once — chat responses with several markers, and the chart legends in
 * PR #353. Shares cache entries with `useInstanceLabel`, and the directory collapses the
 * whole set into one request.
 */
export const useInstanceLabels = (ids: string[]) => {
  const { baseUrl, token, canQuery, isPortalSource } = usePortalAccess();

  // Deduped + sorted so the query list is stable across renders that reorder the input.
  const uniqueIds = useMemo(() => [...new Set(ids.filter(Boolean))].sort(), [ids]);

  const results = useQueries({
    queries: uniqueIds.map((id) => instanceQueryOptions(baseUrl, token, id, canQuery)),
  });

  const byId = useMemo(() => {
    const map = new Map<string, InstanceLabelState>();
    uniqueIds.forEach((id, index) => {
      const result = results[index];
      map.set(id, {
        id,
        instance: result?.data ?? undefined,
        isResolved: !!result?.isSuccess,
        isEnabled: isPortalSource,
      });
    });
    return map;
  }, [uniqueIds, results, isPortalSource]);

  const labelOf = useCallback(
    (id: string) => formatInstanceLabel(byId.get(id) ?? { id, isResolved: false, isEnabled: isPortalSource }),
    [byId, isPortalSource],
  );

  // `isEnabled` here means "the portal is actually the source" (`isPortalSource`), not just
  // "the flag is on" — see the doc comment on `useInstanceLabel` above.
  return { labelOf, isEnabled: isPortalSource };
};
